import { createFileRoute, Link } from '@tanstack/react-router';
import { useBlogPosts, useBlogStatusCounts, type BlogListParams, type AdminBlogPost } from '@/hooks/use-blog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Video,
  Eye,
  Edit3,
  Trash2,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  FileText,
  CheckCircle,
  Edit,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';

// Status configuration
const STATUS_OPTIONS = [
  { key: 'all', label: 'All Posts', countKey: 'all' },
  { key: 'published', label: 'Published', countKey: 'published' },
  { key: 'draft', label: 'Draft', countKey: 'draft' },
  { key: 'archived', label: 'Archived', countKey: 'archived' },
];

// Status badge styles
const STATUS_BADGE_CLASS = {
  published: 'bg-green-100 text-green-800 border-green-200',
  draft: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  archived: 'bg-gray-100 text-gray-800 border-gray-200',
};

// Sort options
const SORT_OPTIONS = [
  { key: 'updated_at', label: 'Latest' },
  { key: 'created_at', label: 'Oldest' },
  { key: 'published_at', label: 'Published Date' },
  { key: 'views', label: 'Most Views' },
  { key: 'title', label: 'Title A-Z' },
];

export const Route = createFileRoute('/blog/')({
  component: BlogPage,
});

function BlogPage() {
  const [filters, setFilters] = useState<BlogListParams>({
    page: 1,
    pageSize: 10,
    status: 'all',
    sortBy: 'updated_at',
    sortDir: 'desc',
  });

  const [searchQuery, setSearchQuery] = useState('');

  // Debounce search
  useMemo(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        setFilters(prev => ({ ...prev, query: searchQuery.trim(), page: 1 }));
      } else {
        setFilters(prev => {
          const { query, ...rest } = prev;
          return { ...rest, page: 1 };
        });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: blogData, isLoading, error } = useBlogPosts(filters);
  const { data: counts } = useBlogStatusCounts();

  // Debug logging
  if (error) {
    console.error('Blog Page Error:', error);
    console.error('Error details:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
  }

  const handleFilterChange = (newFilters: Partial<BlogListParams>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSortChange = (sortBy: string) => {
    const newSortDir = filters.sortBy === sortBy && filters.sortDir === 'desc' ? 'asc' : 'desc';
    setFilters(prev => ({ ...prev, sortBy: sortBy as any, sortDir: newSortDir, page: 1 }));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatViews = (views: number) => {
    if (views >= 1000000) {
      return `${(views / 1000000).toFixed(1)}M`;
    } else if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K`;
    }
    return views.toString();
  };

  return (
    <div className="space-y-6">
      {/* Stats Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Posts</p>
                <p className="text-2xl font-bold text-gray-900">{counts?.all ?? 0}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <span className="text-sm text-green-600">+12%</span>
              <span className="text-sm text-gray-500 ml-2">from last month</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Published</p>
                <p className="text-2xl font-bold text-gray-900">{counts?.published ?? 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <span className="text-sm text-green-600">+8%</span>
              <span className="text-sm text-gray-500 ml-2">from last month</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-2xl font-bold text-gray-900">{counts?.draft ?? 0}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Edit className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <span className="text-sm text-yellow-600">+3</span>
              <span className="text-sm text-gray-500 ml-2">new this week</span>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Views Today</p>
                <p className="text-2xl font-bold text-gray-900">
                  {blogData?.posts?.reduce((sum, post) => sum + (post.views || 0), 0) ?? 0}
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Eye className="h-6 w-6 text-purple-600" />
              </div>
            </div>
            <div className="mt-4 flex items-center">
              <span className="text-sm text-green-600">+24%</span>
              <span className="text-sm text-gray-500 ml-2">from yesterday</span>
            </div>
          </div>
        </div>

        {/* Posts Table Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Table Header with Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">All Posts</h3>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Search posts..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                  />
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                </div>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange({ status: e.target.value as any })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
                <select
                  value={filters.category || ''}
                  onChange={(e) => handleFilterChange({ category: e.target.value || undefined })}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                >
                  <option value="">All Categories</option>
                  <option value="Technology">Technology</option>
                  <option value="Design">Design</option>
                  <option value="Business">Business</option>
                  <option value="Marketing">Marketing</option>
                  <option value="Tutorial">Tutorial</option>
                  <option value="AI">AI</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 font-medium mb-2">Error loading blog posts</p>
              <p className="text-sm text-red-400">
                {error instanceof Error ? error.message : String(error)}
              </p>
              <details className="mt-4 text-left max-w-2xl mx-auto">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Show error details
                </summary>
                <pre className="mt-2 p-4 bg-gray-100 rounded text-xs overflow-auto">
                  {JSON.stringify(error, null, 2)}
                </pre>
              </details>
            </div>
          ) : blogData?.posts?.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No blog posts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tags</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {blogData?.posts?.map((post) => (
                    <tr key={post.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          {post.thumbnail_url ? (
                            <div className="h-12 w-12 rounded-lg overflow-hidden mr-4 flex-shrink-0">
                              <img className="w-full h-full object-cover" src={post.thumbnail_url} alt={post.title} />
                            </div>
                          ) : (
                            <div className="h-12 w-12 rounded-lg bg-gray-200 mr-4 flex-shrink-0 flex items-center justify-center">
                              <FileText className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <Link 
                              to="/blog/$id" 
                              params={{ id: post.id }}
                              className="text-sm font-medium text-gray-900 hover:text-blue-600 transition-colors cursor-pointer"
                            >
                              {post.title}
                            </Link>
                            {post.excerpt && (
                              <div className="text-sm text-gray-500 mt-1 line-clamp-1">{post.excerpt}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {post.author_avatar ? (
                            <img className="h-8 w-8 rounded-full mr-2" src={post.author_avatar} alt={post.author_name || 'Author'} />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-gray-300 mr-2 flex items-center justify-center">
                              <span className="text-xs font-medium text-gray-600">
                                {post.author_name?.charAt(0)?.toUpperCase() || 'A'}
                              </span>
                            </div>
                          )}
                          <div className="text-sm text-gray-900">{post.author_name || 'Unknown Author'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1 items-center">
                          {post.tags && post.tags.length > 0 ? (
                            <>
                              {post.tags.slice(0, 2).map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                              {post.tags.length > 2 && (
                                <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                                  +{post.tags.length - 2}
                                </span>
                              )}
                            </>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn('px-2 py-1 text-xs font-medium rounded-full', {
                          'bg-green-100 text-green-800': post.status === 'published',
                          'bg-yellow-100 text-yellow-800': post.status === 'draft',
                          'bg-gray-100 text-gray-800': post.status === 'archived',
                        })}>
                          {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(post.updated_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {post.views > 0 ? formatViews(post.views) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <Link to="/blog/$id" params={{ id: post.id }}>
                            <button className="text-blue-600 hover:text-blue-700">
                              <Edit3 className="h-4 w-4" />
                            </button>
                          </Link>
                          <button className="text-gray-400 hover:text-gray-600">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button className="text-red-400 hover:text-red-600">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {blogData && blogData.totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing{' '}
                  <span className="font-medium text-gray-900">
                    {(blogData.page - 1) * blogData.pageSize + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium text-gray-900">
                    {Math.min(blogData.page * blogData.pageSize, blogData.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-gray-900">{blogData.total}</span> posts
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(blogData.page - 1)}
                    disabled={blogData.page <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(5, blogData.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={cn(
                          'px-3 py-2 text-sm font-medium rounded-lg',
                          blogData.page === pageNum
                            ? 'text-white bg-blue-600'
                            : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                        )}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(blogData.page + 1)}
                    disabled={blogData.page >= blogData.totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
    </div>
  );
}

